import { isPlatformBrowser } from '@angular/common';
import { Service, signal, computed, inject, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageKeys } from '@core/types/local-storage.type';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';

import { lastValueFrom } from 'rxjs';

export const I18nLocales = {
  NL_NL: 'nl_NL',
  EN_EN: 'en_EN'
} as const;

export type I18nLocale = (typeof I18nLocales)[keyof typeof I18nLocales];
export interface I18nLanguage {
  locale: I18nLocale;
  label: string;
  countryCode: string;
}

export const AVAILABLE_LANGUAGES: ReadonlyArray<I18nLanguage> = [
  { locale: I18nLocales.EN_EN, label: 'language.english', countryCode: 'EN' }
] as const;

export const defaultLocale = I18nLocales.EN_EN;
@Service()
export class I18nService {
  private readonly translateService = inject(TranslateService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly currentLocale = signal<I18nLocale>(defaultLocale);

  public readonly currentLocaleCode = computed(() => this.currentLocale().split('_')[0]);

  public readonly currentLang = computed(
    () => AVAILABLE_LANGUAGES.find((lang) => lang.locale === this.currentLocale())?.countryCode
  );

  public async initService(): Promise<void> {
    if (!this.isBrowser) {
      this.translateService.setFallbackLang(defaultLocale);
      this.currentLocale.set(defaultLocale);
      return;
    }

    this.translateService.addLangs(AVAILABLE_LANGUAGES.map((lang) => lang.locale));
    this.translateService.setFallbackLang(defaultLocale);

    const localeInStorage = this.localStorageService.get(LocalStorageKeys.Locale);
    let fromLocalStorage = false;

    if (localeInStorage && this.isValidLocale(localeInStorage as string)) {
      this.currentLocale.set(localeInStorage as I18nLocale);
      fromLocalStorage = true;
    }

    await this.useLanguage(this.currentLocale(), fromLocalStorage);
  }

  public checkCountryCodeValidity(countryCode: string): boolean {
    return AVAILABLE_LANGUAGES.some((lang) => lang.countryCode.toLowerCase() === countryCode.toLowerCase());
  }

  public getLocaleByCountryCode(countryCode: string): I18nLocale | undefined {
    return AVAILABLE_LANGUAGES.find((lang) => lang.countryCode.toLowerCase() === countryCode.toLowerCase())?.locale;
  }

  public async useLanguage(locale: I18nLocale, fromLocalStorage = false): Promise<void> {
    if (!this.isBrowser) {
      this.currentLocale.set(locale);
      return;
    }

    try {
      await lastValueFrom(this.translateService.use(locale));
      this.localStorageService.set(LocalStorageKeys.Locale, locale);
      this.currentLocale.set(locale);
    } catch (error) {
      console.error('Error setting language', error);

      if (fromLocalStorage) {
        console.error('Locale in LocalStorage invalid; removing locale from LocalStorage');
        this.localStorageService.remove(LocalStorageKeys.Locale);
      }
    }
  }

  public getLanguages(): ReadonlyArray<I18nLanguage> {
    return AVAILABLE_LANGUAGES;
  }

  private isValidLocale(locale: string): locale is I18nLocale {
    return Object.values(I18nLocales).includes(locale as I18nLocale);
  }
}
